import React, { useEffect, useState, Fragment, createElement } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { ExpenseChart } from '../components/charts/ExpenseChart';
import { ExpenseStatusBadge } from '../components/ui/ExpenseStatusBadge';
import { Avatar } from '../components/ui/Avatar';
import { ExpenseDetailModal } from '../components/modals/ExpenseDetailModal';
import { Pagination } from '../components/ui/Pagination';
import { DollarSignIcon, CheckCircleIcon, ClockIcon, XCircleIcon, UserIcon, PlusIcon, ArrowRightIcon, FileTextIcon, DownloadIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon, CalendarIcon, ArrowUpIcon, ArrowDownIcon, PieChartIcon, BarChart2Icon } from 'lucide-react';
import { mockExpenses, mockEmployees } from '../utils/mockData';
import { formatDate, isValidDate, parseDate } from '../utils/dateUtils';
import { calculateTotalAmount, filterExpensesByDateRange, getExpenseStatsByStatus, formatExpensesForChart } from '../utils/dashboardUtils';
import { formatCurrency, formatDateTime } from '../utils/formatUtils';
import { ChartControls } from '../components/ui/ChartControls';
import { ChartSkeleton } from '../components/charts/ChartSkeleton';
export function AdminDashboard() {
  // State for date range filter
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  // State for expenses and stats
  const [filteredExpenses, setFilteredExpenses] = useState(mockExpenses);
  const [expenseStats, setExpenseStats] = useState({
    pending: {
      count: 0,
      amount: 0
    },
    approved: {
      count: 0,
      amount: 0
    },
    rejected: {
      count: 0,
      amount: 0
    },
    total: {
      count: 0,
      amount: 0
    }
  });
  // State for expense chart data
  const [chartData, setChartData] = useState([]);
  const [chartView, setChartView] = useState('type');
  // State for loading state for chart
  const [chartLoading, setChartLoading] = useState(false);
  // State for sorting expenses
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [expandedRows, setExpandedRows] = useState({});
  // State for selected expense and modal
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedExpenses, setPaginatedExpenses] = useState([]);
  // Apply date range filter
  useEffect(() => {
    // Initialize with all expenses
    let filtered = [...mockExpenses];
    // Validate dates before filtering
    const start = parseDate(dateRange.startDate);
    const end = parseDate(dateRange.endDate);
    if (start && end) {
      // Filter expenses by date range
      filtered = filterExpensesByDateRange(mockExpenses, dateRange.startDate, dateRange.endDate);
    } else {
      console.error('Invalid date range', {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
    }
    // Sort filtered expenses
    const sorted = sortExpenses(filtered, sortField, sortDirection);
    setFilteredExpenses(sorted);
    // Calculate expense statistics
    const stats = getExpenseStatsByStatus(filtered);
    setExpenseStats(stats);
    // Add loading state for chart data
    setChartLoading(true);
    // Simulate network delay for chart data
    setTimeout(() => {
      // Prepare chart data
      const chartDataByView = formatExpensesForChart(filtered, chartView);
      setChartData(chartDataByView);
      setChartLoading(false);
    }, 600);
    // Calculate total pages for pagination
    setTotalPages(Math.ceil(sorted.length / itemsPerPage));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [dateRange, sortField, sortDirection, chartView, itemsPerPage]);
  // Update paginated expenses when page changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedExpenses(filteredExpenses.slice(startIndex, endIndex));
  }, [filteredExpenses, currentPage, itemsPerPage]);
  // Handle date range change
  const handleDateRangeChange = e => {
    const {
      name,
      value
    } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle sort change
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  // Sort expenses
  const sortExpenses = (expenses, field, direction) => {
    return [...expenses].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      // Special handling for dates
      if (field === 'date') {
        aValue = parseDate(aValue);
        bValue = parseDate(bValue);
        // Handle invalid dates
        if (!aValue || !bValue) {
          return 0;
        }
        aValue = aValue.getTime();
        bValue = bValue.getTime();
      }
      // Handle amount as number
      if (field === 'amount') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  // Toggle row expansion
  const toggleRowExpansion = id => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  // Open expense detail modal
  const openExpenseDetail = expense => {
    setSelectedExpense(expense);
    setIsDetailModalOpen(true);
  };
  // Get employee name by ID
  const getEmployeeName = id => {
    const employee = mockEmployees.find(emp => emp.id === id);
    return employee ? employee.name : 'Unknown Employee';
  };
  // Get department name by employee ID
  const getEmployeeDepartment = id => {
    const employee = mockEmployees.find(emp => emp.id === id);
    return employee ? employee.department : 'Unknown Department';
  };
  // Generate quick filters for date ranges
  const applyQuickFilter = days => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - days);
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };
  // Handle page change
  const handlePageChange = page => {
    setCurrentPage(page);
  };
  // Handle items per page change
  const handleItemsPerPageChange = e => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
  };
  // Function to handle chart download
  const handleChartDownload = () => {
    // Create CSV content
    let csvContent = 'data:text/csv;charset=utf-8,';
    if (chartView === 'date') {
      // Headers for time series data
      csvContent += 'Date,Amount\n';
      // Data rows
      chartData.forEach(item => {
        csvContent += `${item.date},${item.amount}\n`;
      });
    } else {
      // Headers for category data
      csvContent += 'Category,Amount\n';
      // Data rows
      chartData.forEach(item => {
        csvContent += `${item.name},${item.value}\n`;
      });
    }
    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `expense-analysis-${chartView}-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    // Trigger download
    link.click();
    document.body.removeChild(link);
  };
  // Function to refresh chart data
  const handleChartRefresh = () => {
    setChartLoading(true);
    setTimeout(() => {
      const chartDataByView = formatExpensesForChart(filteredExpenses, chartView);
      setChartData(chartDataByView);
      setChartLoading(false);
    }, 600);
  };
  return <div className="space-y-6" data-id="element-937">
      {/* Dashboard Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-938">
        <div data-id="element-939">
          <h1 className="text-2xl font-bold text-gray-100" data-id="element-940">Admin Dashboard</h1>
          <p className="text-gray-400 mt-1" data-id="element-941">
            Overview of all expenses and approvals
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-942">
          <Button variant="outline" onClick={() => {
          /* Download CSV logic would go here */
        }} data-id="element-943">
            <DownloadIcon size={16} className="mr-1.5" data-id="element-944" />
            Export CSV
          </Button>
          <Link to="/expense/new" data-id="element-945">
            <Button data-id="element-946">
              <PlusIcon size={16} className="mr-1.5" data-id="element-947" />
              Add Expense
            </Button>
          </Link>
        </div>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-948">
        <Card className="p-4" data-id="element-949">
          <div className="flex items-center" data-id="element-950">
            <div className="h-12 w-12 rounded-full bg-blue-900/30 flex items-center justify-center mr-4" data-id="element-951">
              <DollarSignIcon size={24} className="text-blue-400" data-id="element-952" />
            </div>
            <div data-id="element-953">
              <p className="text-sm text-gray-400" data-id="element-954">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-955">
                {formatCurrency(expenseStats.total.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-956">
                {expenseStats.total.count} expenses
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-957">
          <div className="flex items-center" data-id="element-958">
            <div className="h-12 w-12 rounded-full bg-yellow-900/30 flex items-center justify-center mr-4" data-id="element-959">
              <ClockIcon size={24} className="text-yellow-400" data-id="element-960" />
            </div>
            <div data-id="element-961">
              <p className="text-sm text-gray-400" data-id="element-962">Pending Approval</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-963">
                {formatCurrency(expenseStats.pending.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-964">
                {expenseStats.pending.count} expenses
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-965">
          <div className="flex items-center" data-id="element-966">
            <div className="h-12 w-12 rounded-full bg-green-900/30 flex items-center justify-center mr-4" data-id="element-967">
              <CheckCircleIcon size={24} className="text-green-400" data-id="element-968" />
            </div>
            <div data-id="element-969">
              <p className="text-sm text-gray-400" data-id="element-970">Approved</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-971">
                {formatCurrency(expenseStats.approved.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-972">
                {expenseStats.approved.count} expenses
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4" data-id="element-973">
          <div className="flex items-center" data-id="element-974">
            <div className="h-12 w-12 rounded-full bg-red-900/30 flex items-center justify-center mr-4" data-id="element-975">
              <XCircleIcon size={24} className="text-red-400" data-id="element-976" />
            </div>
            <div data-id="element-977">
              <p className="text-sm text-gray-400" data-id="element-978">Rejected</p>
              <p className="text-2xl font-bold text-gray-100" data-id="element-979">
                {formatCurrency(expenseStats.rejected.amount)}
              </p>
              <p className="text-xs text-gray-400" data-id="element-980">
                {expenseStats.rejected.count} expenses
              </p>
            </div>
          </div>
        </Card>
      </div>
      {/* Expense Chart */}
      <Card data-id="element-981">
        <div className="p-4 border-b border-gray-700" data-id="element-982">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-983">
            <h2 className="text-lg font-medium text-gray-100" data-id="element-984">
              Expense Analysis
            </h2>
            <ChartControls activeView={chartView} onViewChange={setChartView} onDownload={handleChartDownload} onRefresh={handleChartRefresh} viewOptions={[{
            id: 'type',
            label: 'By Type',
            icon: <PieChartIcon size={16} data-id="element-986" />,
            tooltip: 'View expenses by category'
          }, {
            id: 'date',
            label: 'By Date',
            icon: <BarChart2Icon size={16} data-id="element-987" />,
            tooltip: 'View expenses over time'
          }]} data-id="element-985" />
          </div>
        </div>
        <div className="p-4" data-id="element-988">
          {chartLoading ? <ChartSkeleton variant={chartView === 'type' ? 'pie' : 'bar'} data-id="element-989" /> : <ExpenseChart data={chartData} showControls={true} data-id="element-990" />}
        </div>
      </Card>
      {/* Expenses Table */}
      <Card data-id="element-991">
        <div className="p-4 border-b border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-992">
          <h2 className="text-lg font-medium text-gray-100" data-id="element-993">Recent Expenses</h2>
          <div className="flex flex-wrap gap-2" data-id="element-994">
            <button onClick={() => applyQuickFilter(7)} className="px-3 py-1 text-xs rounded-md bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-650" data-id="element-995">
              Last 7 days
            </button>
            <button onClick={() => applyQuickFilter(30)} className="px-3 py-1 text-xs rounded-md bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-650" data-id="element-996">
              Last 30 days
            </button>
            <button onClick={() => applyQuickFilter(90)} className="px-3 py-1 text-xs rounded-md bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-650" data-id="element-997">
              Last 90 days
            </button>
          </div>
        </div>
        {/* Date Range Filter */}
        <div className="p-4 border-b border-gray-700 bg-gray-750" data-id="element-998">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-999">
            <div className="flex-1" data-id="element-1000">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1001">
                Start Date
              </label>
              <div className="relative" data-id="element-1002">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1003">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-1004" />
                </div>
                <input type="date" name="startDate" value={dateRange.startDate} onChange={handleDateRangeChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 text-sm" data-id="element-1005" />
              </div>
            </div>
            <div className="flex-1" data-id="element-1006">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1007">
                End Date
              </label>
              <div className="relative" data-id="element-1008">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1009">
                  <CalendarIcon size={16} className="text-gray-400" data-id="element-1010" />
                </div>
                <input type="date" name="endDate" value={dateRange.endDate} onChange={handleDateRangeChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200 text-sm" data-id="element-1011" />
              </div>
            </div>
          </div>
        </div>
        {/* Items per page selector */}
        <div className="p-4 border-b border-gray-700 bg-gray-750 flex justify-end" data-id="element-1012">
          <div className="flex items-center space-x-2" data-id="element-1013">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-400" data-id="element-1014">
              Show:
            </label>
            <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPageChange} className="bg-gray-700 border border-gray-600 text-gray-200 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 px-2 py-1" data-id="element-1015">
              <option value="10" data-id="element-1016">10</option>
              <option value="25" data-id="element-1017">25</option>
              <option value="50" data-id="element-1018">50</option>
              <option value="100" data-id="element-1019">100</option>
            </select>
          </div>
        </div>
        {/* Expenses Table */}
        <div className="overflow-x-auto" data-id="element-1020">
          <table className="min-w-full divide-y divide-gray-700" data-id="element-1021">
            <thead className="bg-gray-800" data-id="element-1022">
              <tr data-id="element-1023">
                <th className="w-10 px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1024"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('date')} data-id="element-1025">
                  <div className="flex items-center" data-id="element-1026">
                    <span data-id="element-1027">Date</span>
                    {sortField === 'date' && <span className="ml-1" data-id="element-1028">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1029" /> : <ArrowDownIcon size={14} data-id="element-1030" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('employeeId')} data-id="element-1031">
                  <div className="flex items-center" data-id="element-1032">
                    <span data-id="element-1033">Employee</span>
                    {sortField === 'employeeId' && <span className="ml-1" data-id="element-1034">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1035" /> : <ArrowDownIcon size={14} data-id="element-1036" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('type')} data-id="element-1037">
                  <div className="flex items-center" data-id="element-1038">
                    <span data-id="element-1039">Type</span>
                    {sortField === 'type' && <span className="ml-1" data-id="element-1040">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1041" /> : <ArrowDownIcon size={14} data-id="element-1042" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-1043">
                  <div className="flex items-center" data-id="element-1044">
                    <span data-id="element-1045">Amount</span>
                    {sortField === 'amount' && <span className="ml-1" data-id="element-1046">
                        {sortDirection === 'asc' ? <ArrowUpIcon size={14} data-id="element-1047" /> : <ArrowDownIcon size={14} data-id="element-1048" />}
                      </span>}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1049">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-1050">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700" data-id="element-1051">
              {paginatedExpenses.length > 0 ? paginatedExpenses.map(expense => <Fragment key={expense.id} data-id="element-1052">
                    <tr className={`hover:bg-gray-700 transition-colors ${expandedRows[expense.id] ? 'bg-gray-750' : ''} cursor-pointer`} onClick={() => openExpenseDetail(expense)} data-id="element-1053">
                      <td className="px-4 py-4 whitespace-nowrap" onClick={e => {
                  e.stopPropagation();
                  toggleRowExpansion(expense.id);
                }} data-id="element-1054">
                        <button className="text-gray-400 hover:text-gray-200" data-id="element-1055">
                          {expandedRows[expense.id] ? <ChevronUpIcon size={16} data-id="element-1056" /> : <ChevronDownIcon size={16} data-id="element-1057" />}
                        </button>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300" data-id="element-1058">
                        {formatDate(expense.date)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-1059">
                        <div className="flex items-center" data-id="element-1060">
                          <Avatar name={getEmployeeName(expense.employeeId)} id={expense.employeeId} size="sm" className="mr-2" data-id="element-1061" />
                          <div data-id="element-1062">
                            <div className="text-sm font-medium text-gray-200" data-id="element-1063">
                              {getEmployeeName(expense.employeeId)}
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-1064">
                              {getEmployeeDepartment(expense.employeeId)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300 capitalize" data-id="element-1065">
                        {expense.type}
                        {expense.subtype && <span className="text-gray-400" data-id="element-1066">
                            {' '}
                            ({expense.subtype})
                          </span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-200" data-id="element-1067">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" data-id="element-1068">
                        <ExpenseStatusBadge status={expense.status} data-id="element-1069" />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={e => e.stopPropagation()} data-id="element-1070">
                        <div className="flex items-center justify-end space-x-2" data-id="element-1071">
                          <button className={`px-2 py-1 rounded text-xs font-medium ${expense.status === 'pending' ? 'bg-green-900/30 text-green-300 hover:bg-green-900/50' : 'bg-gray-700 text-gray-400'} transition-colors`} disabled={expense.status !== 'pending'} data-id="element-1072">
                            Approve
                          </button>
                          <button className={`px-2 py-1 rounded text-xs font-medium ${expense.status === 'pending' ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-gray-700 text-gray-400'} transition-colors`} disabled={expense.status !== 'pending'} data-id="element-1073">
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[expense.id] && <tr className="bg-gray-750" data-id="element-1074">
                        <td colSpan={7} className="px-4 py-3" data-id="element-1075">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-2" data-id="element-1076">
                            <div data-id="element-1077">
                              <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-1078">
                                Expense Details
                              </h4>
                              <div className="space-y-2" data-id="element-1079">
                                <div className="flex" data-id="element-1080">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1081">
                                    Description:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1082">
                                    {expense.description}
                                  </span>
                                </div>
                                <div className="flex" data-id="element-1083">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1084">
                                    Location:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1085">
                                    {expense.location || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex" data-id="element-1086">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1087">
                                    Expense Date:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1088">
                                    {formatDateTime(expense.date, true)}
                                  </span>
                                </div>
                                <div className="flex" data-id="element-1089">
                                  <span className="text-xs text-gray-400 w-24" data-id="element-1090">
                                    Submitted:
                                  </span>
                                  <span className="text-xs text-gray-200" data-id="element-1091">
                                    {formatDateTime(expense.submittedDate, true)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div data-id="element-1092">
                              <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-1093">
                                Receipt
                              </h4>
                              {expense.receiptUrl ? <div className="border border-gray-600 rounded-md p-2 bg-gray-700 flex items-center justify-center h-24" data-id="element-1094">
                                  <img src={expense.receiptUrl} alt="Receipt" className="max-h-full" data-id="element-1095" />
                                </div> : <div className="border border-gray-600 rounded-md p-2 bg-gray-700 flex items-center justify-center h-24 text-gray-400 text-xs" data-id="element-1096">
                                  No receipt provided
                                </div>}
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>) : <tr data-id="element-1097">
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-400" data-id="element-1098">
                    No expenses found for the selected date range
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredExpenses.length > 0 && <div className="px-4 py-3 border-t border-gray-700 flex justify-between items-center flex-wrap gap-4" data-id="element-1099">
            <div className="text-sm text-gray-400" data-id="element-1100">
              Showing{' '}
              {Math.min(filteredExpenses.length, (currentPage - 1) * itemsPerPage + 1)}{' '}
              to {Math.min(currentPage * itemsPerPage, filteredExpenses.length)}{' '}
              of {filteredExpenses.length} expenses
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} data-id="element-1101" />
            <Link to="/expenses" className="text-blue-400 hover:text-blue-300 text-sm flex items-center ml-auto" data-id="element-1102">
              View All Expenses
              <ArrowRightIcon size={16} className="ml-1" data-id="element-1103" />
            </Link>
          </div>}
      </Card>
      {/* Expense Detail Modal */}
      {selectedExpense && <ExpenseDetailModal isOpen={isDetailModalOpen} onClose={() => setIsDetailModalOpen(false)} expense={selectedExpense} isAdmin={true} onApprove={id => {
      // Handle approve logic
      console.log('Approve expense:', id);
      setIsDetailModalOpen(false);
    }} onReject={(id, reason) => {
      // Handle reject logic
      console.log('Reject expense:', id, 'Reason:', reason);
      setIsDetailModalOpen(false);
    }} data-id="element-1104" />}
    </div>;
}